import { HttpErrorResponse } from "@angular/common/http";
import { AfterContentInit, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from "@angular/core";
import { take, takeUntil } from "rxjs";
import { DestroyService } from "@webapp/core/util/services/destroy.service";
import { Goal } from "@webapp/okrs/goals/models/goal.models";
import { SuggestionDrawerIndicatorStatePropagator } from "@webapp/platform-intelligence/expandable-suggestions/suggestion-drawer/shared/utils/state-propagator";
import { PiImprovementFacade } from "@webapp/platform-intelligence/shared/components/pi-feedback-card/services/improvement/pi-improvement-facade.service";
import { generateImprovementPayload } from "@webapp/platform-intelligence/shared/components/pi-feedback-card/services/improvement/utils";
import { PiStateProcessorInstance } from "@webapp/platform-intelligence/shared/components/pi-feedback-card/services/state-processor/pi-state-processor.models";
import { PiStateProcessorService } from "@webapp/platform-intelligence/shared/components/pi-feedback-card/services/state-processor/pi-state-processor.service";
import { getPiErrorData } from "@webapp/platform-intelligence/shared/utils/pi-errors.utils";
import { PiTrackingFlowNameEnum } from "@webapp/platform-intelligence/shared/utils/pi-tracking";
import { SuggestionDrawerSectionType, SuggestionDrawerSuggestionGroupType, SuggestionDrawerTitleSuggestionParams } from "../shared/models/suggestion-drawer.models";

@Component({
  selector: "pi-create-objective-suggestion-drawer",
  templateUrl: "./create-objective-suggestion-drawer.component.html",
  providers: [DestroyService],
})
export class CreateObjectiveSuggestionDrawerComponent implements OnInit, OnChanges, AfterContentInit {
  @Input() public isOpen = false;
  @Input() public titleSection: SuggestionDrawerSectionType<{ title: SuggestionDrawerTitleSuggestionParams } & SuggestionDrawerSuggestionGroupType>;
  @Input() public statePropagator: SuggestionDrawerIndicatorStatePropagator;
  @Input() public visibilityChange: (isVisible: boolean) => void;
  @Input() public goal: Goal;

  @Input() public flowId: string;
  @Input() public flowName: PiTrackingFlowNameEnum;
  @Output() public readonly suggestionReplace = new EventEmitter<{ id: string; suggestionText: string }>();

  public instance: PiStateProcessorInstance;

  constructor(private piStateProcessorService: PiStateProcessorService, private destroyService$: DestroyService, private piImprovementFacade: PiImprovementFacade) {}

  public ngAfterContentInit(): void {
    if (this.flowId && this.flowName) {
      this.piStateProcessorService.assigneInstanceFlowProperties(this.instance.id, { flowId: this.flowId, flowName: this.flowName });
    }
  }

  public ngOnInit(): void {
    this.instance = this.piStateProcessorService.createNewInstance({
      type: "goal",
      subType: "createObjectiveTitle",
      isInline: false,
      iconType: "goal",
      iconTheme: "fill",
      improveSuggestionContext: this.titleSection.improveSuggestionContext,
    });

    this.instance.subject.pipe(takeUntil(this.destroyService$)).subscribe((data) => {
      if (data.suggestAnother) {
        this.titleSection.onSuggestAnother();
      }

      if (data.refreshError) {
        this.titleSection.onRetry();
      }

      if (data.addSuggestion) {
        this.titleSection.onAcceptSuggestion();
      }

      if (data.goBackToSuggestion) {
        this.titleSection.goBackToLastAvailableSuggestion();
      }
    });

    this.subscribeToImprovementSuggestion();
    this.subscribeToReplacementSuggestion();
  }

  private subscribeToReplacementSuggestion(): void {
    this.instance.replacementSuggestionSubject.pipe(takeUntil(this.destroyService$)).subscribe((data) => {
      this.suggestionReplace.emit({ id: data.newSuggestion.id, suggestionText: data.newSuggestion.text });
    });
  }

  private subscribeToImprovementSuggestion(): void {
    this.instance.improvementSubject.pipe(takeUntil(this.destroyService$)).subscribe((data) => {
      const suggestion = this.titleSection.suggestions[0];

      const payload = generateImprovementPayload({
        subEntityType: this.instance.subType,
        instruction: data.improvement,
        suggestion: {
          title: suggestion.suggestionText,
        },
        isAskAI: true,
        improveSuggestionContext: this.instance.improveSuggestionContext,
      });

      this.titleSection.isLoading = true;

      this.piImprovementFacade
        .postImprovement<{ id: string; title: string }>(payload)
        .pipe(take(1))
        .subscribe({
          next: (data) => {
            suggestion.id = data.suggestions.improvement.id;
            suggestion.suggestionText = data.suggestions.improvement.title;

            this.titleSection.isLoading = false;
          },
          error: (err: HttpErrorResponse) => {
            this.titleSection.errorData = getPiErrorData("title", err);

            this.titleSection.isLoading = false;
            this.titleSection.hasError = true;
          },
        });
    });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (this.statePropagator && this.titleSection) {
      const isLoading = this.titleSection.isLoading;
      const suggestionStateLevel = undefined;
      const isSuggestionAvailable = !this.titleSection.title.isTitleAvailable;
      const hasRequiredDependencies = !this.titleSection.title.isTitleAvailable && this.titleSection.title.isOwnerAvailable && this.titleSection.title.isSessionAvailable;

      this.statePropagator.emitStateChange({ isLoading, suggestionStateLevel, hasRequiredDependencies, isSuggestionAvailable });
    }

    if (changes.titleSection && changes.titleSection.currentValue && this.instance) {
      this.piStateProcessorService.updateInstanceImproveSuggestionContext(this.instance.id, changes.titleSection.currentValue.improveSuggestionContext);
    }
  }
}
